<template>
  <section v-if="enabled" class="dc-passport pa-3" data-testid="car-passport" :aria-busy="loading || busy">
    <car-template-assign :car="car" :templates="templates ? templates.templates : []" :ready="loaded && !loading" @assigned="$emit('template-assigned', $event)" />
    <v-progress-linear v-if="loading" indeterminate />
    <v-alert v-if="errorText" type="warning" outlined role="alert">{{ errorText }}</v-alert>
    <v-btn text :disabled="loading || busy" data-testid="passport-refresh" @click="loadPassport">{{ $t('custom.update') }}</v-btn>
    <template v-if="loaded">
      <section v-for="group in groups" :key="group.id" :data-testid="`passport-${group.id}`" class="mt-4">
        <h3 class="text-h6">{{ $t(group.title) }}</h3>
        <p v-if="!group.entries.length" class="text--secondary mt-2">{{ $t(group.empty) }}</p>
        <article v-for="entry in group.entries" :key="entry.id" class="dc-passport-entry" :data-testid="`passport-entry-${entry.id}`">
          <button v-if="entry.evidence_url" type="button" class="dc-passport-thumbnail" data-testid="open-evidence"
                  :aria-label="$t('damage_control.passport.open_evidence') + ': ' + zoneTitle(entry)" @click="selected = entry">
            <passport-evidence :entry="entry" :title="zoneTitle(entry)" @preview-error="refreshEvidence" />
          </button>
          <p v-else class="text--secondary">{{ $t('damage_control.review.no_preview') }}</p>
          <div class="dc-passport-details">
            <h4>{{ zoneTitle(entry) }}</h4>
            <p class="mb-1">{{ $t(damageTypeKey(entry.damage_type)) }}</p>
            <p v-if="entry.severity" class="mb-1">{{ $t('damage_control.passport.severity') }}: {{ entry.severity }}</p>
            <p class="mb-1">{{ $t('damage_control.passport.created_at') }}: <time :datetime="entry.created_at">{{ formatDate(entry.created_at) }}</time></p>
            <p class="mb-1">{{ $t(statusKey(entry)) }}</p>
            <p v-if="entry.pre_module" class="mb-1">{{ $t('damage_control.act.pre_module') }}</p>
            <p v-if="entry.decided_via === 'agent'" class="mb-1">{{ $t('damage_control.review.source_agent') }}</p>
            <div v-if="isActive(entry)" class="dc-passport-actions mt-2">
              <!-- Same permission as CarCard car-edit methods and Fleet::UpdateCar. -->
              <v-btn v-if="isStaff" outlined small :disabled="loading || busy" :data-testid="`repair-${entry.id}`" @click="mutate(entry, 'repair')">{{ $t('damage_control.passport.mark_repaired') }}</v-btn>
              <v-btn v-if="isManagerPlus" text small :disabled="loading || busy" :data-testid="`revoke-${entry.id}`" @click="mutate(entry, 'revoke')">{{ $t('damage_control.passport.revoke') }}</v-btn>
            </div>
          </div>
        </article>
      </section>
    </template>
    <v-dialog :value="!!selected" max-width="1100" scrollable @input="!$event && (selected = null)">
      <v-card v-if="selected" data-testid="passport-evidence-dialog">
        <v-card-title class="dc-passport-dialog-title">{{ zoneTitle(selected) }}</v-card-title>
        <v-card-text><passport-evidence :entry="selected" :title="zoneTitle(selected)" @preview-error="refreshEvidence" /></v-card-text>
        <v-card-actions><v-spacer /><v-btn text @click="selected = null">{{ $t('custom.close') }}</v-btn></v-card-actions>
      </v-card>
    </v-dialog>
  </section>
</template>

<script>
import { damageControlApi, parseDamageControlError, reportDamageControlFailure } from '@/api/damageControl';
import roleMixin from '@/mixins/roleMixin';
import damageControlAccessMixin from '@/mixins/damageControlAccessMixin';
import { damageTypeKey } from '../review/presentation';
import PassportEvidence from './PassportEvidence.vue';
import { shouldRefreshDamageControlPreview } from '@/utils/damageControlPreview';
import CarTemplateAssign from './CarTemplateAssign.vue';
import { readInitialSection } from '@/components/dashboard/cards/initialSection';

const STATUS_KEYS = {
  active: 'damage_control.passport.status_active',
  repaired: 'damage_control.passport.status_repaired',
  withdrawn: 'damage_control.passport.status_withdrawn',
  archived: 'damage_control.passport.status_archived',
};
const isArchived = entry => entry.archived === true || Boolean(entry.archived_at) || entry.status === 'archived';
const isActive = entry => entry.status === 'active' && entry.active !== false && !isArchived(entry);

export default {
  name: 'CarPassport',
  components: { PassportEvidence, CarTemplateAssign },
  mixins: [roleMixin, damageControlAccessMixin],
  props: {
    car: { type: Object, required: true },
    initialSection: { type: Object, default: undefined },
    initialEntityId: { type: [Number, String], default: undefined },
  },
  data: () => ({ entries: [], templates: null, selected: null, loading: false, loaded: false,
    appliedInitialContext: null, appliedInitialEntity: null, busy: false, errorText: '', loadSequence: 0, disposed: false, failedPreviewUrls: [] }),
  computed: {
    initialData() { return [this.contextKey, this.initialSection, this.initialEntityId]; },
    enabled() { return this.damageControlAvailable; },
    contextKey() { return JSON.stringify([this.damageControlCompany && this.damageControlCompany.id, this.damageControlCompany && this.damageControlCompany.rental_object, this.car.id, this.enabled]); },
    groups() {
      return [
        { id: 'active', title: 'damage_control.passport.active', empty: 'damage_control.passport.empty_active', entries: this.entries.filter(isActive) },
        { id: 'history', title: 'damage_control.passport.history', empty: 'damage_control.passport.empty_history', entries: this.entries.filter(entry => !isActive(entry)) },
      ];
    },
    zoneTitles() {
      const titles = Object.create(null);
      if (!this.templates) return titles;
      const standard = (this.templates.standard || []).filter(template => template.rental_object === this.damageControlCompany.rental_object);
      // Branch titles override matching standard keys; historical branch-only keys remain resolvable.
      [...standard, ...(this.templates.templates || [])].forEach(template => {
        (template.zones || []).forEach(zone => { titles[zone.key] = zone.title; });
      });
      return titles;
    },
  },
  watch: {
    initialData: { immediate: true, handler() { this.initializePassport(); } },
  },
  beforeDestroy() { this.disposed = true; this.loadSequence += 1; },
  methods: {
    initializePassport() {
      if (this.appliedInitialContext === this.contextKey) return;
      this.busy = false;
      const initial = readInitialSection(this.initialSection);
      // A consumed envelope belongs to its original company/context. Subsequent
      // context changes keep the legacy refresh, rather than restoring that snapshot.
      if (!initial.provided || this.appliedInitialEntity === String(this.car.id)) {
        this.appliedInitialContext = this.contextKey;
        this.loadPassport();
        return;
      }
      // An envelope for the previous car must never trigger an autonomous GET.
      this.appliedInitialContext = null;
      this.loadSequence += 1;
      this.entries = []; this.templates = null; this.selected = null;
      this.loaded = false; this.loading = false; this.errorText = '';
      if (this.disposed || !this.enabled || !this.car.id) return;
      if (this.initialEntityId !== undefined && String(this.initialEntityId) !== String(this.car.id)) return;
      this.appliedInitialContext = this.contextKey;
      this.appliedInitialEntity = String(this.car.id);
      if (initial.status === 'ok' || initial.status === 'empty') {
        const data = initial.data || {};
        this.entries = (data.passport && data.passport.entries) || [];
        this.templates = data.templates || { standard: [], templates: [] };
        this.loaded = true;
      } else if (initial.status === 'error') {
        this.errorText = this.$t('damage_control.errors.request_failed');
      } else {
        this.errorText = this.$t(initial.status === 'forbidden'
          ? 'damage_control.errors.forbidden' : 'damage_control.errors.damage_control_disabled');
      }
    },
    damageTypeKey,
    refreshEvidence(url) { if (shouldRefreshDamageControlPreview(this.failedPreviewUrls, url)) this.loadPassport({ preserveSelection: true }); },
    isActive,
    zoneTitle(entry) { return this.zoneTitles[entry.zone_key] || this.$t('damage_control.passport.zone_deleted'); },
    statusKey(entry) { return isArchived(entry) ? STATUS_KEYS.archived : STATUS_KEYS[entry.status] || STATUS_KEYS.withdrawn; },
    formatDate(value) {
      if (!value) return '—';
      const date = new Date(value);
      return Number.isNaN(date.getTime()) ? '—' : date.toLocaleString(this.$i18n.locale);
    },
    async loadPassport({ preserveSelection = false } = {}) {
      const sequence = ++this.loadSequence;
      const context = this.contextKey;
      const selectedId = preserveSelection && this.selected ? this.selected.id : null;
      const current = () => !this.disposed && this.enabled && context === this.contextKey && sequence === this.loadSequence;
      this.entries = []; this.templates = null;
      if (!preserveSelection) this.selected = null;
      this.loaded = false; this.loading = false; this.errorText = '';
      if (!this.enabled || !this.car.id || this.disposed) return;
      this.loading = true;
      try {
        const [passport, templates] = await Promise.all([
          damageControlApi.getPassport(this.axios, this.car.id),
          damageControlApi.listTemplates(this.axios),
        ]);
        if (!current()) return;
        this.entries = passport.entries || []; this.templates = templates; this.loaded = true;
        if (selectedId != null) this.selected = this.entries.find(entry => String(entry.id) === String(selectedId)) || null;
      } catch (error) {
        if (current()) this.errorText = reportDamageControlFailure(this, error, { silent: true });
      } finally {
        if (current()) this.loading = false;
      }
    },
    canAct(entry, action) {
      return this.enabled && this.loaded && !this.loading && isActive(entry) && this.entries.some(item => item.id === entry.id && isActive(item)) &&
        ((action === 'repair' && this.isStaff) || (action === 'revoke' && this.isManagerPlus));
    },
    async mutate(entry, action) {
      if (this.busy || !this.canAct(entry, action)) return;
      const context = this.contextKey;
      const current = () => !this.disposed && this.enabled && context === this.contextKey;
      this.busy = true;
      this.errorText = '';
      try {
        const confirmed = await this.$confirm({
          title: this.$t(action === 'repair' ? 'damage_control.passport.mark_repaired' : 'damage_control.passport.revoke'),
          text: this.$t(action === 'repair' ? 'damage_control.passport.confirm_repaired' : 'damage_control.passport.confirm_revoke'),
          confirmButtonText: this.$t('custom.yes'), cancelButtonText: this.$t('custom.no'),
        });
        if (!confirmed || !current() || !this.canAct(entry, action)) return;
        if (action === 'repair') await damageControlApi.markRepaired(this.axios, this.car.id, entry.id);
        else await damageControlApi.revokePassportEntry(this.axios, this.car.id, entry.id);
        if (current()) await this.loadPassport();
      } catch (error) {
        if (!current()) return;
        const parsed = parseDamageControlError(error);
        if (parsed && parsed.status === 409 && parsed.code === 'conflict') {
          await this.loadPassport();
          if (current() && !this.errorText) this.errorText = this.$t('damage_control.passport.inactive');
        } else {
          this.errorText = reportDamageControlFailure(this, error, { silent: true });
        }
      } finally {
        if (current()) this.busy = false;
      }
    },
  },
};
</script>

<style scoped>
.dc-passport { min-width: 0; }
.dc-passport-entry { display: flex; gap: 16px; padding: 16px 0; border-bottom: 1px solid rgba(128, 128, 128, .3); align-items: flex-start; }
.dc-passport-thumbnail { display: block; width: 280px; max-width: 100%; flex-shrink: 0; cursor: zoom-in; border-radius: 4px; overflow: hidden; }
.dc-passport-thumbnail:focus-visible { outline: 3px solid #1976d2; outline-offset: 3px; }
.dc-passport-details { min-width: 0; overflow-wrap: anywhere; }
.dc-passport-actions { display: flex; flex-wrap: wrap; gap: 8px; }
.dc-passport-actions .v-btn { max-width: 100%; height: auto; min-height: 36px; padding-top: 8px; padding-bottom: 8px; }
.dc-passport-actions ::v-deep .v-btn__content { white-space: normal; }
.dc-passport-dialog-title { overflow-wrap: anywhere; word-break: normal; }
@media (max-width: 600px) {
  .dc-passport-entry { flex-direction: column; }
  .dc-passport-thumbnail, .dc-passport-details, .dc-passport-actions .v-btn { width: 100%; }
}
</style>
